import { useMemo, useRef, useState } from 'react';

import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { cn, normalizeColorHex } from '@/lib/utils';
import { EntityAvatar } from '@/modules/common';
import { ScrollableText } from '@/modules/planner/components/assignment/text/scrollable-text';
import { formatDisplayDate } from '@/utils/date-formatters';

import { getFilterOption } from './store/filter-labels-store';
import {
  FilterChipsProps,
  RangeModeFilterConfig,
  StaticAutocompleteFilterConfig,
  TableFilterConfig,
} from './types/types';
import { parseDateRangeFilterValue } from './utils/date-range-filter-value';
import { parseRangeModeFilterValue } from './utils/range-mode-filter-value';

/** One selected option's display data for the chip's hover tooltip. */
interface ChipOption {
  /** The stored filter value — used to remove just this option from the filter. */
  value: string;
  label: string;
  color?: string;
  avatar?: string | null;
}

interface ChipDisplayText {
  label: string;
  displayValue: string;
  /**
   * Resolved data for EVERY selected value (multi-select autocomplete types).
   * When more than one is selected the chip collapses to "first +N"; this holds
   * the full list (label + optional color/avatar) so the chip can reveal it in a
   * hover tooltip. Empty for single-value/non-autocomplete chips.
   */
  allOptions: ChipOption[];
}

/**
 * Get display text for filter chip
 */
function getChipDisplayText(
  config: TableFilterConfig,
  values: string[],
  t: (key: string, options?: Record<string, unknown>) => string
): ChipDisplayText {
  const label = config.label;

  if (config.type === 'search') {
    return { label, displayValue: values[0] || '', allOptions: [] };
  }

  if (config.type === 'dateRange') {
    const parsed = parseDateRangeFilterValue(values[0]);
    const displayValue = parsed
      ? `${formatDisplayDate(parsed.startDate)} – ${formatDisplayDate(parsed.endDate)}`
      : values[0] || '';
    return { label, displayValue, allOptions: [] };
  }

  // Boolean switch — show the label only (no trailing value)
  if (config.type === 'switch') {
    return { label, displayValue: '', allOptions: [] };
  }

  // Range mode — render the "mode amount unit" stored as "mode:amount[:strict]"
  if (config.type === 'rangeMode') {
    const parsed = parseRangeModeFilterValue(values[0]);
    const modeLabel =
      (config as RangeModeFilterConfig).modes.find(m => m.value === parsed?.mode)?.label ?? '';
    const unit = (config as RangeModeFilterConfig).unit ?? '';
    const strictSuffix =
      parsed && !parsed.isInclusive ? ` ${t('tableFilters.rangeNotIncluding')}` : '';
    return {
      label,
      displayValue: `${modeLabel} ${parsed?.amount ?? ''}${unit}${strictSuffix}`.trim(),
      allOptions: [],
    };
  }

  // Builds the chip's display from a list of resolved options. The chip text
  // collapses to "first +N"; the full list is carried for the hover tooltip.
  const buildFromOptions = (options: ChipOption[], fallbackDisplay: string): ChipDisplayText => {
    if (options.length === 0) {
      return { label, displayValue: fallbackDisplay, allOptions: [] };
    }
    if (options.length === 1) {
      return { label, displayValue: options[0].label, allOptions: [] };
    }
    return {
      label,
      displayValue: `${options[0].label} +${options.length - 1}`,
      allOptions: options,
    };
  };

  // For static autocomplete, read options (label + color) straight from config.
  if (config.type === 'autocomplete') {
    const options: ChipOption[] = values
      .map(v => (config as StaticAutocompleteFilterConfig).options.find(o => o.value === v))
      .filter(Boolean)
      .map(o => ({
        value: o!.value,
        label: o!.label,
        color: normalizeColorHex(o!.color),
        avatar: o!.avatar,
      }));
    return buildFromOptions(options, values.join(', '));
  }

  // For dynamic autocomplete, read cached option metadata (label + color/avatar);
  // pinned topOptions (e.g. "No department") resolve straight from the config.
  if (config.type === 'dynamic-autocomplete') {
    const options: ChipOption[] = values
      .map(v => {
        const pinned = config.topOptions?.find(opt => opt.value === v);
        if (pinned) {
          return { value: v, label: pinned.label, color: undefined, avatar: undefined };
        }
        const cached = getFilterOption(config.name, v);
        if (!cached) return undefined;
        return {
          value: v,
          label: cached.label,
          color: normalizeColorHex(cached.color),
          avatar: cached.avatar,
        };
      })
      .filter(Boolean) as ChipOption[];

    if (options.length > 0) {
      return buildFromOptions(options, '');
    }
  }

  // Fallback: show values count
  return {
    label,
    displayValue: t('tableFilters.nSelected', { count: values.length }),
    allOptions: [],
  };
}

interface FilterChipProps {
  config: TableFilterConfig;
  values: string[];
  /** Remove the whole filter, or a single value when `value` is provided. */
  onRemove: (value?: string) => void;
  onClick?: () => void;
}

function FilterChip({ config, values, onRemove, onClick }: FilterChipProps) {
  const { t } = useTranslation('common');
  const displayText = useMemo(() => {
    return getChipDisplayText(config, values, t);
  }, [config, values, t]);

  // When the chip collapses multiple selections to "first +N", reveal the full
  // list on hover so the user can see everything they picked without opening the
  // filter. Only shown when there's more than one resolved option.
  const allOptions = displayText.allOptions;
  const hasHiddenSelections = allOptions.length > 1;
  // Show an avatar when ANY option carries one (entity filters like member/project);
  // otherwise fall back to a color dot when colors are present (status filters).
  const showAvatars = allOptions.some(o => o.avatar);
  const showColorDots = !showAvatars && allOptions.some(o => o.color);

  // The full-selection panel opens on hover but holds INTERACTIVE content (a
  // per-value remove button + scroll-on-hover names), so it uses a Popover
  // rather than a Tooltip — tooltip content isn't reliably clickable.
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Refs to the two hover regions: the chip wrapper and the (portaled) panel.
  // The panel renders in a portal, so it isn't a DOM descendant of the chip —
  // when it pops up beneath the cursor (approaching from below/left) the chip
  // fires mouseleave even though the pointer is still "on" the chip+panel group.
  // Re-checking real :hover state when the close timer fires makes the close
  // decision independent of mouseenter/mouseleave event ordering, which is what
  // caused the panel to flash open then immediately close.
  const chipWrapperRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const openPanel = () => {
    clearCloseTimeout();
    setIsPanelOpen(true);
  };

  const scheduleClosePanel = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      // Only close if the pointer has genuinely left BOTH the chip and the panel.
      const stillHovered =
        !!chipWrapperRef.current?.matches(':hover') || !!panelRef.current?.matches(':hover');
      if (!stillHovered) setIsPanelOpen(false);
    }, 200);
  };

  const triggerButton = (
    <button
      type="button"
      onClick={config.disabled ? undefined : onClick}
      disabled={config.disabled}
      className={cn(
        'rounded-sm text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        config.disabled && 'cursor-default'
      )}
    >
      {displayText.displayValue ? (
        <>
          <span className="text-muted-foreground">{displayText.label}:</span>{' '}
          <span className="font-medium">{displayText.displayValue}</span>
        </>
      ) : (
        // Value-less chips (e.g. boolean switches) show the label only
        <span className="font-medium">{displayText.label}</span>
      )}
    </button>
  );

  return (
    // Hover over the chip reveals the full-selection panel (only when there are
    // hidden selections). Handlers live on the wrapper so the whole chip is the
    // hover target; the panel keeps itself open via its own handlers. Hover is a
    // pure enhancement here — the chip's own controls (open filter, remove) stay
    // keyboard-accessible — so the static-interaction lint rule doesn't apply.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={chipWrapperRef}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md bg-muted p-2',
        'text-sm text-foreground'
      )}
      onMouseEnter={hasHiddenSelections ? openPanel : undefined}
      onMouseLeave={hasHiddenSelections ? scheduleClosePanel : undefined}
    >
      {hasHiddenSelections ? (
        <Popover open={isPanelOpen} onOpenChange={setIsPanelOpen}>
          {/* Anchor (not Trigger) so the chip button keeps its own click-to-open-
              filter behavior; the panel is driven purely by hover state. */}
          <PopoverAnchor asChild>{triggerButton}</PopoverAnchor>
          <PopoverContent
            ref={panelRef}
            side="bottom"
            align="start"
            // No offset + a transparent top "bridge" (pt-2 on the content, wrapping
            // the visible surface) so there's no dead zone between the chip and the
            // panel — the pointer can travel in from any direction without the
            // hover snapping shut.
            sideOffset={0}
            // Keep hover open while the pointer is over the panel, and don't steal
            // focus from the page when it opens on hover.
            onMouseEnter={openPanel}
            onMouseLeave={scheduleClosePanel}
            onOpenAutoFocus={e => e.preventDefault()}
            // Inverted surface: dark in light mode, light in dark mode (matches the
            // default tooltip look) via the primary token.
            className="w-60 border-0 bg-transparent p-0 pt-2 shadow-none"
          >
            <ul className="max-h-72 space-y-0.5 overflow-y-auto rounded-md bg-primary p-1 text-primary-foreground shadow-md">
              {allOptions.map((option, index) => (
                <li
                  key={`${option.value}-${index}`}
                  className="flex min-w-0 items-center gap-2 rounded-sm px-1 py-1 text-sm hover:bg-primary-foreground/10"
                >
                  {/* Remove just this value from the filter. Locked filters can't
                      be edited, so the per-item remove button is hidden there. */}
                  {!config.disabled && (
                    <button
                      type="button"
                      aria-label={t('tableFilters.removeValue', { value: option.label })}
                      onClick={() => onRemove(option.value)}
                      className="flex-shrink-0 rounded-sm opacity-80 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {showAvatars ? (
                    <EntityAvatar
                      name={option.label}
                      image={option.avatar ?? undefined}
                      size="xs"
                      className="flex-shrink-0"
                    />
                  ) : showColorDots ? (
                    <span
                      className="h-2.5 w-2.5 flex-shrink-0 rounded-full border border-border"
                      style={{ backgroundColor: option.color ?? 'transparent' }}
                    />
                  ) : (
                    <span className="flex-shrink-0 opacity-70">•</span>
                  )}
                  {/* min-w-0 clips the scrolling text so long names reveal on hover
                      instead of stretching the panel. */}
                  <ScrollableText className="min-w-0 flex-1 whitespace-nowrap">
                    {option.label}
                  </ScrollableText>
                </li>
              ))}
            </ul>
          </PopoverContent>
        </Popover>
      ) : (
        triggerButton
      )}
      {/* Locked filters (config.disabled) cannot be removed. */}
      {!config.disabled && (
        <button
          type="button"
          onClick={() => onRemove()}
          className="ml-0.5 rounded-sm text-foreground ring-offset-background transition-colors hover:text-foreground/70 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

interface ExtendedFilterChipsProps extends FilterChipsProps {
  showClearButton?: boolean;
}

/**
 * Component displaying active filters as removable chips
 */
export function FilterChips({
  filtersConfig,
  activeFilters,
  onRemoveFilter,
  onClearAll,
  onChipClick,
  showClearButton = true,
}: ExtendedFilterChipsProps) {
  const { t } = useTranslation('common');

  // Create a map of filter name to config for quick lookup
  const configMap = useMemo(() => new Map(filtersConfig.map(c => [c.name, c])), [filtersConfig]);

  // Get active filter entries (filter out empty values and search filters)
  const activeFilterEntries = useMemo(() => {
    return Object.entries(activeFilters)
      .filter(([name, value]) => {
        const config = configMap.get(name);
        if (!config) return false;
        // Skip search filters in chips display
        if (config.type === 'search') return false;
        // Skip hidden-from-summary switches (e.g. "Hide archived")
        if (config.type === 'switch' && config.hiddenFromSummary) return false;
        // Check if value is non-empty
        if (Array.isArray(value)) return value.length > 0;
        return value && value.trim() !== '';
      })
      .map(([name, value]) => ({
        name,
        values: Array.isArray(value) ? value : [value],
        config: configMap.get(name)!,
      }));
  }, [activeFilters, configMap]);

  if (activeFilterEntries.length === 0) {
    return null;
  }

  return (
    <>
      {activeFilterEntries.map(({ name, values, config }) => (
        <FilterChip
          key={name}
          config={config}
          values={values}
          onRemove={value => onRemoveFilter(name, value)}
          onClick={onChipClick ? () => onChipClick(name) : undefined}
        />
      ))}
      {showClearButton && (
        <Button variant="ghost" size="sm" onClick={onClearAll} className="h-auto px-2 py-1 text-sm">
          {t('tableFilters.clear')}
        </Button>
      )}
    </>
  );
}
